"use client"

import * as React from "react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const generateHourlyData = () => {
  const data = []
  for (let hour = 0; hour < 24; hour++) {
    // Simular generación solar: mayor producción durante horas centrales del día
    const baseValue = hour >= 6 && hour <= 18 ? 
      50 + Math.sin((hour - 6) * Math.PI / 12) * 100 : 0;
    
    // Hoy: valores con ligera variación
    const todayValue = baseValue * (0.9 + Math.random() * 0.2);
    
    // Mañana: valores ligeramente diferentes para simulación
    const tomorrowValue = baseValue * (0.85 + Math.random() * 0.25);
    
    data.push({
      hour: `${hour}:00`,
      today: Math.round(todayValue),
      tomorrow: Math.round(tomorrowValue),
    })
  }
  return data
}

const chartData = generateHourlyData()

const chartConfig = {
  energy: {
    label: "Energía",
  },
  today: {
    label: "Hoy",
    color: "var(--chart-1)",
  },
  tomorrow: {
    label: "Mañana",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig

export function DailyGenerationChart() {
  const [timeRange, setTimeRange] = React.useState("24h")

  // Filtrar datos según rango de tiempo si es necesario
  const filteredData = chartData

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5">
        <div className="grid flex-1 gap-1">
          <CardTitle>Generación diaria</CardTitle>
          <CardDescription>
            Estimación de generación calculada por nuestra IA
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <AreaChart data={filteredData}>
            <defs>
              <linearGradient id="fillToday" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-today)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-today)"
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="fillTomorrow" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-tomorrow)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-tomorrow)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="hour"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={16}
              tickFormatter={(value) => {
                return value
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    return `Hora: ${value}`
                  }}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="today"
              type="natural"
              fill="url(#fillToday)"
              stroke="var(--color-today)"
              stackId="a"
            />
            <Area
              dataKey="tomorrow"
              type="natural"
              fill="url(#fillTomorrow)"
              stroke="var(--color-tomorrow)"
              stackId="a"
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}